import React, { FC, useEffect, useRef, useState } from 'react';
import { Form, Formik, FormikProps } from 'formik';
import { getEnv } from 'mobx-state-tree';
import { useTheme } from '@mui/material/styles';
import { toast } from 'react-toastify';
import { Observer } from 'mobx-react';
import classNames from 'classnames';

import { Modal } from '@shared/components/Modal';
import { Button } from '@shared/components/Button';
import { FormikTextInput } from '@shared/components/forms/formikWrappers/FormikTextInput';
import { FormikSelectDropdown } from '@shared/components/forms/formikWrappers/FormikSelectDropdown';
import { SelectOption } from '@shared/components/SelectDropdown/Option';
import { convertOptionsToStrings, getOptionsByNameId } from '@shared/helpers/form';
import { OptionType } from '@shared/components/SelectDropdown';
import { ToastMessage } from '@shared/components/Toast';
import { FormError } from '@shared/components/FormError';
import { Avatar } from '@shared/components/Avatar';
import { DeleteConfirmPopover } from '@shared/components/DeleteConfirmPopover';
import { useUsersGridUIStore, useUserUIStore, useProcedureUIStore, useProceduresUIStore } from '@core/useStores';
import { IStoresEnv } from '@core/storesEnv';

import ClearIcon from '@mui/icons-material/Clear';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import CloseIcon from '@assets/close-icon.svg'

import { validationSchema } from './validationSchema';
import { valuesSchema } from './valuesSchema';

import { IUserStore } from 'Users/stores';
import { messages } from 'Users/users.messages';

import { useStyles } from './UserModal.styles';
import { ThemeProps } from '@styles/theme';
import { ProcedureVersionAssignment } from 'Procedures/services';
import { FormikProcedureAssignment } from '../FormikProcedureAssignment';

export const COMMON_SELECT_PROPS = {
    isSearchable: false,
    hideSelectedOptions: false,
    isClearable: false,
    isMulti: false,
    closeMenuOnSelect: true,
    components: { Option: SelectOption },
}

export type UserModalProps = {
    isOpen: boolean,
    onRequestClose: () => void,
}

export class UserFormValues {
    email = '';
    firstName = '';
    lastName = '';
    department = '';
    roleId = { value: '', label: '' };
    groups = [] as OptionType[];
    procedures = [] as {
        value: {
            id: string,
            version: string|null
        },
        label: string
    }[];
}

export const UserModal: FC<UserModalProps> = ({
    isOpen,
    onRequestClose,
}) => {
    const theme = useTheme<ThemeProps>();
    const styles = useStyles({ theme });

    const [userData, setUserData] = useState<IUserStore | null>(null);
    const [userAvatarFromGrid, setUserAvatarFromGrid] = useState<string | null>(null);
    const [deleteAnchorEl, setDeleteAnchorEl] = useState<HTMLElement | null>(null);
    const deleteButtonRef = useRef<HTMLDivElement>(null);
    const [proceduresData, setProceduresData] = useState<{
        value: {
            id: string,
            version: string|null
        },
        label: string
    }[]>([]);

    const usersGridUIStore = useUsersGridUIStore();
    const userUIStore = useUserUIStore();
    const { filtersOptions } = getEnv<IStoresEnv>(usersGridUIStore);

    const assignableRolesOptions = filtersOptions.getAssignableRolesOptions;
    const groupsOptions = filtersOptions.getGroupsOptions;
    const proceduresOptions = filtersOptions.getProceduresOptions;

    const isEditMode = !!userUIStore.userToEditId;
    const [isLoading, setIsLoading] = useState<boolean>(isEditMode);

    const procedureUIStore = useProcedureUIStore();
    const proceduresUIStore = useProceduresUIStore();

    useEffect(() => {
        const getUserData = async () => {
            if (isEditMode) {
                const result = await userUIStore.loadUser({ id: userUIStore.userToEditId });

                if (result.success) {
                    const proceduresList = await Promise.all(result.data.procedures.map( async (p: any) : Promise<any> => {
                        const procedure = await procedureUIStore.loadProcedure(p.id);

                        if(procedure){
                            const procedureUsers = procedure.data.users;
                            const modalUser = procedureUsers.find((u: any) => u.id == result.data.id);
                            if(modalUser){
                                const assignedVersion = modalUser.data.assignedVersion ? modalUser.data.assignedVersion : null;

                                return {
                                    value: {id: procedure.data.id, version: assignedVersion}, label: procedure.data.name,
                                }
                            }else{
                                return null;
                            }
                        }else{
                            return null;
                        }
                    }))

                    if(proceduresList){
                        setProceduresData(proceduresList);
                    }

                    setUserData(result.data);

                    // Avatar and status come from grid search data (single-user GET may not include them)
                    const gridUser = usersGridUIStore.users.find(u => u.id === userUIStore.userToEditId);
                    if (gridUser?.avatar) {
                        setUserAvatarFromGrid(gridUser.avatar);
                    }
                    // Patch status from grid user if missing from single-user response
                    if (gridUser?.status?.name && !result.data.status?.name) {
                        result.data.status = gridUser.status;
                    }
                }
                setIsLoading(false);
            }
        }

        getUserData();
        proceduresUIStore.load();
    }, [isEditMode]);

    const onDeleteUser = () => {
        setDeleteAnchorEl(deleteButtonRef.current);
    };

    const onDeleteUserConfirm = async () => {
        setDeleteAnchorEl(null);
        const result = await userUIStore.deleteUser({ id: userUIStore.userToEditId });
        if (!result.success) {
            toast.error(
                <ToastMessage
                    message={messages['usersPage.removeUser.toast.error']}
                    type='error'
                />
            );
        } else {
            toast.success(
                <ToastMessage
                    message={messages['usersPage.removeUser.toast.success']}
                    type='success'
                />
            );
            usersGridUIStore.setParams({ page: 0 });
            usersGridUIStore.load();
            usersGridUIStore.loadStatistics();
            filtersOptions.loadFilterOptions();
        }
        userUIStore.clearEditUserId();
        userUIStore.toggleUserModalOpen(false);
    };

    const onSubmit = async (values: UserFormValues) => {
        const { email, firstName, lastName, department, ...rest } = values;

        const computedValues = {
            email,
            firstName,
            lastName,
            department: department.trim(),
            roleId: rest.roleId ? rest.roleId.value : '',
            groupIds: rest.groups ? convertOptionsToStrings(rest.groups) : [],
            //procedureIds: rest.procedures ? rest.procedures.map(p => p.value) : [],
        }

        const assignments: Array<ProcedureVersionAssignment> = [];

        proceduresUIStore.procedures.forEach(procedure => {
            const groups = procedure.groups;
            const groupsIds = groups.map(g => g.id);

            const users = procedure.users;

            procedure.versions.forEach(version => {
                const versionGroupsIds = groups.filter(g => g.data?.assignedVersion == version.id).map((g: any) => g.id);
                const versionUsersIds = users.filter(g => g.data?.assignedVersion == version.id).map((g: any) => g.id);

                const versionToAssign = rest.procedures.find(p => p.value.version == version.id && p.value.id == procedure.id);
                const assignedToVersion = users.find(u => u.data?.assignedVersion == version.id && u.id == userData?.id);

                if(!versionToAssign && assignedToVersion){
                    const userToRemove = versionUsersIds.find(id => id == userData?.id);
                    const userIdx = versionUsersIds.indexOf(userToRemove);
                    versionUsersIds.splice(userIdx, 1);
                }else if(versionToAssign && !assignedToVersion){
                    versionUsersIds.push(userData?.id);
                }

                assignments.push({
                    id: procedure.id,
                    versionId: version.id,
                    userIds: versionUsersIds,
                    groupIds: versionGroupsIds.length == 0 ? [] : groupsIds,
                });

            });

            const versionGroupsIds = groups.filter(g => g.data?.assignedVersion == null).map((g: any) => g.id);
            const versionUsersIds = users.filter(g => g.data?.assignedVersion == null).map((g: any) => g.id);

            const versionToAssign = rest.procedures.find(p => p.value.version == null && p.value.id == procedure.id);
            const assignedToVersion = users.find(u => u.data?.assignedVersion == null && u.id == userData?.id);

            if(!versionToAssign && assignedToVersion){
                const userToRemove = versionUsersIds.find(id => id == userData?.id);
                const userIdx = versionUsersIds.indexOf(userToRemove);
                versionUsersIds.splice(userIdx, 1);

            }else if(versionToAssign && !assignedToVersion){
                versionUsersIds.push(userData?.id);
            }
            assignments.push({
                id: procedure.id,
                versionId: null,
                userIds: versionUsersIds,
                groupIds: versionGroupsIds.length == 0 ? [] : groupsIds,
            });
        });

        const assigmentResult = await procedureUIStore.saveVersionAssignments(assignments);

        const result = isEditMode
            ? await userUIStore.editUser({ id: userUIStore.userToEditId, ...computedValues })
            : await usersGridUIStore.inviteUser.sendInviteUserRequest(computedValues);

        const showAssignmentError = () => {
            if (assigmentResult?.success) return;

            toast.error(
                <ToastMessage
                    message={assigmentResult?.error?.body?.messages?.[0]
                        || messages['userModal.assignments.toast.error']}
                    type='error'
                />
            );
        };

        if (result.success) {
            usersGridUIStore.setParams({
                page: 0,
            });

            usersGridUIStore.load();
            filtersOptions.loadFilterOptions();

            onRequestClose();

            toast.success(
                <ToastMessage
                    message={messages['userModal.submit.toast.success'](isEditMode)}
                    type='success'
                />
            );
            showAssignmentError();
        } else {
            onRequestClose();
            toast.error(
                <ToastMessage
                    message={result.error?.body?.messages?.[0]
                        || messages['userModal.submit.toast.error']}
                    type='error'
                />
            );
            showAssignmentError();
        }
    }

    const initialValues = (isEditMode && userData) ? {
        email: userData.email || '',
        firstName: userData.firstName || '',
        lastName: userData.lastName || '',
        department: userData.department || '',
        roleId: { value: userData.roles[0]?.id, label: userData.roles[0]?.name },
        procedures: proceduresData,
        groups: getOptionsByNameId(userData.groups, groupsOptions),
    } : new UserFormValues();

    const firstName = userData?.firstName || '';
    const lastName = userData?.lastName || '';

    const gridUserStatus = usersGridUIStore.users.find(u => u.id === userUIStore.userToEditId)?.status?.name ?? '';
    const isNotRegistered = isEditMode && (
        /invite/i.test(userData?.status?.name ?? '') ||
        /invite/i.test(gridUserStatus)
    );

    const handleSendInvite = async () => {
        if (!userData) {return;}
        const result = await usersGridUIStore.inviteUser.resendInviteUser({
            id: userData.id,
            email: userData.email,
        });
        if (result.success) {
            toast.success(
                <ToastMessage
                    message={messages['resendInviteModal.success.toast']}
                    type='success'
                />
            );
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onRequestClose={onRequestClose}
            className={styles.modal}
            shouldCloseOnOverlayClick={false}
            shouldCloseOnEsc
            noPadding
        >
            <div className={styles.modalContainer}>
                <div className={styles.modalHeader}>
                    <div className={styles.headerContent}>
                        {isEditMode && (
                            <Avatar
                                firstName={firstName}
                                lastName={lastName}
                                image={userAvatarFromGrid || userData?.avatar}
                                className={styles.avatar}
                            />
                        )}
                        <div>
                            <h2 className={styles.h2}>
                                {isEditMode ? `${firstName} ${lastName}` : messages['userModal.newUser']}
                            </h2>
                            {isEditMode && userData?.email && (
                                <p className={styles.headerSubtitle}>{userData.email}</p>
                            )}
                        </div>
                    </div>
                    <div className={styles.headerRight}>
                        {isNotRegistered && (
                            <button
                                type="button"
                                className={styles.sendInviteBtn}
                                onClick={handleSendInvite}
                            >
                                <MailOutlineIcon fontSize="small" />
                                Send Invite
                            </button>
                        )}
                        <div className={styles.imageContainer} onClick={onRequestClose}>
                            <img src={CloseIcon} />
                        </div>
                    </div>
                </div>
                <div className={styles.modalBody}>
                    {isLoading ? (
                        <div>
                            {[0, 1, 2].map(i => (
                                <div key={i} className={styles.skeletonSection}>
                                    <div className={styles.skeletonTitle} />
                                    <div className={styles.skeletonField} />
                                    <div className={styles.skeletonRow}>
                                        <div className={styles.skeletonFieldHalf} />
                                        <div className={styles.skeletonFieldHalf} />
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <Formik
                            onSubmit={onSubmit}
                            initialValues={initialValues}
                            validationSchema={validationSchema}
                            validateOnBlur={false}
                            validateOnChange={false}
                            enableReinitialize={true}
                        >
                            {(form: FormikProps<UserFormValues>) => {
                                const fields = usersGridUIStore.inviteUser.getInviteUserFields;
                                const storeErrors = userUIStore.userUIData.errors || fields.errors;

                                const formErrors: Record<string, any> = form.errors;
                                const errors = Object.keys(formErrors).length
                                    ? Object.keys(formErrors)
                                        .map((key: string) => formErrors[key].value ? formErrors[key].value : formErrors[key])
                                    : storeErrors;

                                return (
                                    <Form className={styles.form} autoComplete='off' noValidate>
                                        <Observer>
                                            {() => (
                                                <>
                                                    {errors?.length && (
                                                        <div className={styles.errors}>
                                                            {errors.map((text: string) => (
                                                                <FormError className={styles.error} key={text} text={text} />
                                                            ))}
                                                        </div>
                                                    )}
                                                </>
                                            )}
                                        </Observer>
                                        <div className={styles.section}>
                                            <div className={styles.sectionTitle}>Personal Information</div>
                                            <div className={styles.field}>
                                                <FormikTextInput
                                                    schema={valuesSchema.email}
                                                    cleanable
                                                />
                                            </div>
                                            <div className={styles.fieldWrapper}>
                                                <div className={classNames(styles.field, styles.halfWidth)}>
                                                    <FormikTextInput schema={valuesSchema.firstName} />
                                                </div>
                                                <div className={classNames(styles.field, styles.halfWidth)}>
                                                    <FormikTextInput schema={valuesSchema.lastName} />
                                                </div>
                                            </div>
                                            <div className={styles.fieldWrapper}>
                                                <div className={classNames(styles.field, styles.halfWidth)}>
                                                    <FormikTextInput schema={valuesSchema.department} />
                                                </div>
                                                <div className={classNames(styles.field, styles.halfWidth)}>
                                                    <FormikSelectDropdown
                                                        schema={valuesSchema.roleId}
                                                        className={styles.dropdown}
                                                        {...COMMON_SELECT_PROPS}
                                                        options={assignableRolesOptions}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                        <div className={styles.section}>
                                            <div className={styles.sectionTitle}>Access</div>
                                            <div className={styles.field}>
                                                <FormikSelectDropdown
                                                    schema={valuesSchema.groups}
                                                    className={styles.dropdown}
                                                    {...COMMON_SELECT_PROPS}
                                                    isMulti
                                                    closeMenuOnSelect={false}
                                                    options={groupsOptions}
                                                />
                                            </div>
                                        </div>
                                        <div className={styles.section}>
                                            <div className={styles.sectionTitle}>Procedures</div>
                                            <div className={styles.field}>
                                                <FormikProcedureAssignment
                                                    schema={valuesSchema.procedures}
                                                    className={styles.dropdown}
                                                    {...COMMON_SELECT_PROPS}
                                                    isMulti
                                                    closeMenuOnSelect={false}
                                                    options={proceduresOptions}
                                                />
                                            </div>
                                        </div>
                                        <div className={classNames(styles.buttons, { [styles.end]: !isEditMode })}>
                                            {
                                                isEditMode && (
                                                    <div ref={deleteButtonRef}>
                                                        <Button
                                                            onClick={onDeleteUser}
                                                            color="error"
                                                            startIcon={<ClearIcon />}
                                                        >
                                                            {messages['userModal.buttons.delete']}
                                                        </Button>
                                                        <DeleteConfirmPopover
                                                            anchorEl={deleteAnchorEl}
                                                            onConfirm={onDeleteUserConfirm}
                                                            onCancel={() => setDeleteAnchorEl(null)}
                                                            message={messages['usersPage.delete.message']}
                                                            confirmText={messages['usersPage.delete.confirmText']}
                                                        />
                                                    </div>
                                                )
                                            }
                                            <div>
                                                <Button
                                                    onClick={onRequestClose}
                                                    className={styles.button} color="secondary"
                                                >
                                                    {messages['userModal.buttons.cancel']}
                                                </Button>
                                                <Button
                                                    className={styles.button}
                                                    type="submit"
                                                >
                                                    {messages['userModal.buttons.submit'](isEditMode)}
                                                </Button>
                                            </div>
                                        </div>
                                    </Form>
                                )
                            }}
                        </Formik>
                    )}
                </div>
            </div>
        </Modal>
    )
}
